// src/components/layout/Header.jsx
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Search, Moon, Sun, Bell, ChevronRight, User, Settings, LogOut } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';

// Page title mapping
const pageTitles = {
  '/': 'Dashboard',
  '/connectors': 'Connectors',
  '/dashboards': 'Dashboards',
  '/charts': 'Charts',
  '/explorer': 'Data Explorer',
  '/model': 'Model',
  '/logs': 'Logs',
  '/admin': 'Admin',
  '/settings': 'Settings',
};

// Breadcrumb generation for nested routes
const getBreadcrumbs = (pathname) => {
  const parts = pathname.split('/').filter(Boolean);
  
  if (parts.length <= 1) return null;
  
  const breadcrumbs = parts.map((part, index) => {
    const path = '/' + parts.slice(0, index + 1).join('/');
    return {
      label: pageTitles[path] || part.charAt(0).toUpperCase() + part.slice(1),
      path,
      isLast: index === parts.length - 1,
    };
  });
  
  return breadcrumbs;
};

export default function Header() {
  const location = useLocation();
  const [searchFocused, setSearchFocused] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [theme, setTheme] = useState('dark');

  // Get user info
  const userEmail = localStorage.getItem('user_email') || 'admin@example.com';
  const userName = userEmail.split('@')[0];
  const userInitials = userName.substring(0, 2).toUpperCase();

  // Get page title and breadcrumbs
  const pageTitle = pageTitles[location.pathname] || 'Dashboard';
  const breadcrumbs = getBreadcrumbs(location.pathname);

  // Keyboard shortcut: Cmd/Ctrl + K for search
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        document.getElementById('global-search')?.focus();
      }

      if (e.key === 'Escape' && document.activeElement?.id === 'global-search') {
        document.getElementById('global-search')?.blur();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  const handleSignOut = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_email');
    localStorage.removeItem('user_roles');
    window.location.href = '/login';
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchValue.trim()) {
      console.log('Search:', searchValue);
    }
  };

  return (
    <header className="sticky top-0 z-40 flex items-center h-16 px-8 bg-[#0a0a0a] border-b border-[#2a2a2a]">
      {/* Left: Page Title & Breadcrumbs */}
      <div className="flex items-center gap-3 min-w-0 flex-shrink-0">
        {breadcrumbs ? (
          <nav className="flex items-center gap-2 text-sm min-w-0">
            {breadcrumbs.map((crumb, index) => (
              <React.Fragment key={crumb.path}>
                {index > 0 && (
                  <ChevronRight className="w-4 h-4 text-[#6a6a6a] flex-shrink-0" />
                )}
                <span
                  className={cn(
                    'truncate',
                    crumb.isLast
                      ? 'font-semibold text-white'
                      : 'text-[#a0a0a0] hover:text-white cursor-pointer transition-colors'
                  )}
                >
                  {crumb.label}
                </span>
              </React.Fragment>
            ))}
          </nav>
        ) : (
          <h1 className="text-lg font-semibold text-white truncate">
            {pageTitle}
          </h1>
        )}
      </div>

      {/* Center: Search */}
      <div className="flex-1 flex justify-center px-8">
        <form onSubmit={handleSearch} className="w-full max-w-md">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6a6a6a] pointer-events-none" />
            <input
              id="global-search"
              type="text"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="Search..."
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
              className={cn(
                'w-full h-9 pl-9 pr-16 bg-[#141414] border text-white text-sm rounded-lg',
                'placeholder:text-[#6a6a6a]',
                'focus:outline-none transition-all',
                searchFocused
                  ? 'border-blue-500 ring-2 ring-blue-500/20'
                  : 'border-[#2a2a2a] hover:border-[#3a3a3a]'
              )}
            />
            <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
              <kbd className="hidden sm:inline-flex items-center px-1.5 h-5 bg-[#1a1a1a] border border-[#2a2a2a] rounded text-[10px] text-[#6a6a6a] font-mono">
                ⌘K
              </kbd>
            </div>
          </div>
        </form>
      </div>

      {/* Right: Actions */}
      <div className="flex items-center gap-2 flex-shrink-0">
        {/* Theme Toggle */}
        <button
          onClick={toggleTheme}
          className="flex items-center justify-center w-9 h-9 text-[#a0a0a0] hover:text-white hover:bg-[#1a1a1a] rounded-lg transition-colors"
          title="Toggle theme"
        >
          {theme === 'dark' ? (
            <Moon className="w-5 h-5" />
          ) : (
            <Sun className="w-5 h-5" />
          )}
        </button>

        {/* Notifications */}
        <button
          className="relative flex items-center justify-center w-9 h-9 text-[#a0a0a0] hover:text-white hover:bg-[#1a1a1a] rounded-lg transition-colors"
          title="Notifications"
        >
          <Bell className="w-5 h-5" />
          <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-blue-500 rounded-full ring-2 ring-[#0a0a0a]" />
        </button>

        {/* Profile Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-2.5 pl-1 pr-3 h-9 hover:bg-[#1a1a1a] rounded-lg transition-colors">
              <Avatar className="w-7 h-7">
                <AvatarFallback className="bg-gradient-to-br from-blue-500 to-violet-500 text-white text-xs font-semibold">
                  {userInitials}
                </AvatarFallback>
              </Avatar>
              <span className="text-sm font-medium text-white hidden md:inline">
                {userName}
              </span>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="w-56 bg-[#1a1a1a] border-[#2a2a2a] text-white"
          >
            <DropdownMenuLabel className="text-[#a0a0a0]">
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium text-white">{userName}</p>
                <p className="text-xs text-[#6a6a6a]">{userEmail}</p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator className="bg-[#2a2a2a]" />
            <DropdownMenuItem className="text-[#a0a0a0] hover:text-white hover:bg-[#222222] cursor-pointer">
              <User className="mr-2 h-4 w-4" />
              <span>Profile</span>
            </DropdownMenuItem>
            <DropdownMenuItem className="text-[#a0a0a0] hover:text-white hover:bg-[#222222] cursor-pointer">
              <Settings className="mr-2 h-4 w-4" />
              <span>Settings</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator className="bg-[#2a2a2a]" />
            <DropdownMenuItem
              className="text-red-400 hover:text-red-300 hover:bg-[#222222] cursor-pointer"
              onClick={handleSignOut}
            >
              <LogOut className="mr-2 h-4 w-4" />
              <span>Sign out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}